import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import MyContext from '@/shared/contexts/AppContext';
import { AttendanceSheetsFilters } from './PerMonthFilters';
import { AttendanceSheetsModals } from './PerMonthModals';
import { AttendanceSheetsTable } from './PerMonthTable';
import { MassNotificationModal } from '../../components/ModalNotificacaoMassa';
import { useAttendanceSheets } from '../perMonth/usePerMonth';
import {
  deveOcultarMenusCompletosListagemFolha,
  getAttendanceSheetIdFromJwt,
  isJwtSessaoSemUsuarioId,
} from '@/modules/system/auth/application/auth.service';
import { getUltimaFolhaEstagiarioPorToken } from '../../../attendance-sheet.service';
import { useContext } from 'react';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { AttendanceSheetsPerMonthPdf } from '../../components/AttendanceSheetsPerMonthPDF';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useNavigate } from 'react-router-dom';

export function AttendanceSheetsPerMonthPage() {
  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  const navigate = useNavigate();
  const redirectEstagiarioTokenRef = useRef(false);

  const { setActions } = useBreadcrumbActions();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[] | null>(null);

  const tableState = useDataTableState('estagiario.nome');
  const {
    mesBusca,
    setMesBusca,
    statusBusca,
    setStatusBusca,
    filtroResponsavel,
    setFiltroResponsavel,
    filtroEstagiario,
    setFiltroEstagiario,
    filtrosStatus,
    registros,
    handleBuscar,
    comFolha,
    setComFolha,
    responsaveis,
    estagiarios,
    permissaoCriarFolhas,
    permissaoExcluirFolhas,
    isExecuting,
    handleCriarFolha,
    handleGenerateAttendanceSheetsForMonth,
    selectedItem,
    setSelectedItem,
    folhaSelecionada,
    setFolhaSelecionada,
    showModalCriarFolha,
    setShowModalCriarFolha,
    showModalCriarFolhas,
    setShowModalCriarFolhas,
    showModalNotificacaoIndividual,
    setShowModalNotificacaoIndividual,
    showModalNotificacoes,
    setShowModalNotificacoes,
    showModalResponsavel,
    setShowModalResponsavel,
    showModalAssinaturas,
    setShowModalAssinaturas,
    folhaParaAssinaturas,
    setFolhaParaAssinaturas,
    loaded,
  } = useAttendanceSheets(tableState);

  useEffect(() => {
    if (redirectEstagiarioTokenRef.current) return;
    if (!isJwtSessaoSemUsuarioId()) return;
    if (!context.state.cpf?.trim()) return;
    redirectEstagiarioTokenRef.current = true;

    const irParaFolha = (id: string) =>
      navigate(`${Links.Folha_Frequencia_Editar}/${id}`, { replace: true });

    void getUltimaFolhaEstagiarioPorToken()
      .then(({ id }) => {
        if (id) irParaFolha(id);
        else {
          const idJwt = getAttendanceSheetIdFromJwt();
          if (idJwt) irParaFolha(idJwt);
          else redirectEstagiarioTokenRef.current = false;
        }
      })
      .catch(() => {
        const idJwt = getAttendanceSheetIdFromJwt();
        if (idJwt) irParaFolha(idJwt);
        else redirectEstagiarioTokenRef.current = false;
      });
  }, [context.state.cpf, navigate]);

  const podeNotificar = useMemo(
    () => Boolean(context.state?.username && registros && registros.length > 0),
    [context.state?.username, registros],
  );

  useEffect(() => {
    if (!loaded) return;

    const menusCompactosEstagiarioLink = deveOcultarMenusCompletosListagemFolha(context.state.username);

    if (menusCompactosEstagiarioLink) {
      setActions({
        primary: podeNotificar
          ? {
              label: 'Notificações',
              variant: 'primary',
              onClick: () => setShowModalNotificacoes(true),
            }
          : undefined,
        secondary: {
          label: 'Pendências',
          onClick: () => {
            window.open(Links.Folha_Frequencia_Pendencias, '_blank', 'noopener,noreferrer');
          },
        },
        print: {
          label: 'Imprimir',
          onClick: () => {
            if (!registros) return;
            exportFromJsxToPdf(
              <AttendanceSheetsPerMonthPdf
                mesBusca={mesBusca}
                registrosVisiveis={registros[0] ?? []}
                totalRegistros={registros[1] ?? 0}
                visibleColumnKeys={visibleColumnKeys}
              />,
            );
          },
        },
        history: {
          label: 'Sair',
          onClick: () => {
            window.location.href = Links.Auth_Logout;
          },
        },
      });
    } else {
      setActions({
        primary: podeNotificar
          ? {
              label: 'Notificações',
              variant: 'primary',
              onClick: () => setShowModalNotificacoes(true),
            }
          : undefined,
        secondary: {
          label: 'Pendências',
          onClick: () => {
            window.open(Links.Folha_Frequencia_Pendencias, '_blank', 'noopener,noreferrer');
          },
        },
        print: {
          label: 'Imprimir',
          onClick: () => {
            if (!registros) return;
            exportFromJsxToPdf(
              <AttendanceSheetsPerMonthPdf
                mesBusca={mesBusca}
                registrosVisiveis={registros[0] ?? []}
                totalRegistros={registros[1] ?? 0}
                visibleColumnKeys={visibleColumnKeys}
              />,
            );
          },
        },
        history: {
          label: 'Sair',
          dropdownOptions: [
            {
              label: 'Escolha de módulo',
              onClick: () => {
                window.location.href = Links.Escolha_Modulo;
              },
            },
            {
              label: 'Sair',
              onClick: () => {
                window.location.href = Links.Auth_Logout;
              },
            },
          ],
          onClick: () => {},
        },
      });
    }

    return () => setActions({});
  }, [
    loaded,
    mesBusca,
    podeNotificar,
    registros,
    context.state.username,
    setActions,
    setShowModalNotificacoes,
    visibleColumnKeys,
  ]);

  if (!loaded) return <Loading />;

  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <h3 className="pt-4">Folhas de Frequências</h3>
          <span className="blockquote-footer pb-4 text-end">
            Registros de faltas justificadas e presenças com horários de
            entrada e saída.
          </span>
        </div>

        <div className="col-md-4">
          <br />
        </div>
      </div>
      <hr />

      <AttendanceSheetsFilters
        mesBusca={mesBusca}
        setMesBusca={setMesBusca}
        filtrosStatus={filtrosStatus}
        statusBusca={statusBusca}
        setStatusBusca={setStatusBusca}
        filtroResponsavel={filtroResponsavel}
        setFiltroResponsavel={setFiltroResponsavel}
        filtroEstagiario={filtroEstagiario}
        setFiltroEstagiario={setFiltroEstagiario}
        responsaveis={responsaveis}
        estagiarios={estagiarios}
        comFolha={comFolha}
        setComFolha={setComFolha}
        permissaoCriarFolhas={permissaoCriarFolhas}
        setShowModalCriarFolhas={setShowModalCriarFolhas}
        tableState={tableState}
      />

      {registros && (
        <AttendanceSheetsTable
          registros={registros}
          tableState={tableState}
          handleBuscar={handleBuscar}
          setSelectedItem={setSelectedItem}
          setShowModalCriarFolha={setShowModalCriarFolha}
          setFolhaSelecionada={setFolhaSelecionada}
          setShowModalResponsavel={setShowModalResponsavel}
          setShowModalNotificacaoIndividual={setShowModalNotificacaoIndividual}
          setFolhaParaAssinaturas={setFolhaParaAssinaturas}
          setShowModalAssinaturas={setShowModalAssinaturas}
          permissaoExcluirFolhas={permissaoExcluirFolhas}
          onVisibleColumnsChange={(keys) => setVisibleColumnKeys(keys)}
        />
      )}

      <AttendanceSheetsModals
        folhaSelecionada={folhaSelecionada}
        selectedItem={selectedItem}
        showModalResponsavel={showModalResponsavel}
        setShowModalResponsavel={setShowModalResponsavel}
        showModalNotificacaoIndividual={showModalNotificacaoIndividual}
        setShowModalNotificacaoIndividual={setShowModalNotificacaoIndividual}
        showModalCriarFolha={showModalCriarFolha}
        setShowModalCriarFolha={setShowModalCriarFolha}
        showModalCriarFolhas={showModalCriarFolhas}
        setShowModalCriarFolhas={setShowModalCriarFolhas}
        showModalNotificacoes={showModalNotificacoes}
        setShowModalNotificacoes={setShowModalNotificacoes}
        isExecuting={isExecuting}
        handleCriarFolha={handleCriarFolha}
        handleGenerateAttendanceSheetsForMonth={
          handleGenerateAttendanceSheetsForMonth
        }
        registros={registros}
        mesBusca={mesBusca}
        showModalAssinaturas={showModalAssinaturas}
        setShowModalAssinaturas={setShowModalAssinaturas}
        folhaParaAssinaturas={folhaParaAssinaturas}
      />

      {registros && registros.length > 0 && (
        <MassNotificationModal
          isOpen={showModalNotificacoes}
          onClose={() => setShowModalNotificacoes(false)}
          registrosVisiveis={registros?.[0] ?? []}
        />
      )}
    </>
  );
}
